<template>
    <div class="content mk-aff-report">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_report") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="mb-3">
                <div class="row">
                    <div class="col-xl-4 col-md-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.commission.range") }}</label>
                        <el-date-picker v-model="range" type="daterange" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" :clearable="false" unlink-panels data-testid="aff-rep-range"></el-date-picker>
                    </div>
                    <div class="col-xl-2 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.report.group") }}</label>
                        <el-select v-model="group" class="w-100">
                            <el-option value="day" :label="$t('marketing.affiliate.report.group_day')"></el-option>
                            <el-option value="month" :label="$t('marketing.affiliate.report.group_month')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-3 col-6 form-group">
                        <label class="form-control-label">{{ $t("marketing.affiliate.report.export_which") }}</label>
                        <el-select v-model="exportReport" class="w-100">
                            <el-option value="series" :label="$t('marketing.affiliate.report.export_series')"></el-option>
                            <el-option value="top-affiliates" :label="$t('marketing.affiliate.report.top_affiliates')"></el-option>
                            <el-option value="top-targets" :label="$t('marketing.affiliate.report.top_targets')"></el-option>
                        </el-select>
                    </div>
                    <div class="col-xl-3 col-md-6 form-group d-flex align-items-end" style="gap: 8px;">
                        <base-button type="primary" size="sm" :loading="loading" data-testid="aff-rep-search" @click="run"><i class="fa fa-search"></i> {{ $t("marketing.affiliate.search") }}</base-button>
                        <base-button type="secondary" size="sm" :loading="exporting" :disabled="schemaMissing" data-testid="aff-rep-export" @click="exportCsv"><i class="fa fa-file-csv"></i> {{ $t("marketing.affiliate.export_csv") }}</base-button>
                    </div>
                </div>
                <small v-if="rangeError" class="text-danger">{{ $t("marketing.affiliate.err.invalid_range") }}</small>
            </card>

            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-3" :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>

            <template v-if="cards">
                <div class="row">
                    <div v-for="k in cardKeys" :key="k" class="col-xl-2 col-md-4 col-6">
                        <card class="mb-3" body-classes="py-3">
                            <div class="text-muted small">{{ $t("marketing.affiliate.report.card." + k) }}</div>
                            <div class="h3 mb-0" :data-card="k">{{ cardValue(k) }}</div>
                        </card>
                    </div>
                </div>

                <card class="mb-3">
                    <h4 slot="header" class="mb-0">{{ $t("marketing.affiliate.report.chart_title") }}</h4>
                    <div class="chart" style="height: 280px;">
                        <line-chart v-if="chart.labels.length" :key="chartKey" :height="280" :chart-data="chart" :extra-options="chartOptions"></line-chart>
                        <p v-else class="text-muted">{{ $t("marketing.affiliate.empty") }}</p>
                    </div>
                </card>

                <div class="row">
                    <div class="col-xl-6">
                        <card class="mb-3" body-classes="px-0 pb-0">
                            <h4 slot="header" class="mb-0">{{ $t("marketing.affiliate.report.top_affiliates") }}</h4>
                            <el-table :data="topAffiliates" size="small" class="aff-table bon-table bon-tight" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-rep-top-aff">
                                <el-table-column class-name="bon-tight" label="Affiliate" min-width="100">
                                    <template v-slot="{ row }"><b>{{ row.code }}</b><div class="small text-muted">{{ row.name }}</div></template>
                                </el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.clicks')" width="72" align="right" prop="clicks"></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.orders')" width="80" align="right" prop="orders"></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.base')" width="96" align="right"><template v-slot="{ row }">{{ money(row.base_total) }}</template></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.commission')" width="106" align="right"><template v-slot="{ row }">{{ money(row.commission) }}</template></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.zero')" width="84" align="right">
                                    <template v-slot="{ row }"><span :class="{ 'text-danger': row.zero_paid_ad || row.zero_self }">{{ row.zero_paid_ad || 0 }} / {{ row.zero_self || 0 }}</span></template>
                                </el-table-column>
                            </el-table>
                        </card>
                    </div>
                    <div class="col-xl-6">
                        <card class="mb-3" body-classes="px-0 pb-0">
                            <h4 slot="header" class="mb-0">{{ $t("marketing.affiliate.report.top_targets") }}</h4>
                            <el-table :data="topTargets" size="small" class="aff-table bon-table bon-tight" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-rep-top-targets">
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.commission.product')" min-width="160">
                                    <template v-slot="{ row }">
                                        {{ row.name }}
                                        <small class="text-muted">({{ tk("marketing.affiliate.rule.scope_", (row.target_type === "promotion" ? "promotion" : "product")) }} #{{ row.target_id }})</small>
                                    </template>
                                </el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.clicks')" width="72" align="right" prop="clicks"></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.orders')" width="80" align="right" prop="orders"></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.base')" width="96" align="right"><template v-slot="{ row }">{{ money(row.base_total) }}</template></el-table-column>
                                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.report.col.commission')" width="106" align="right"><template v-slot="{ row }">{{ money(row.commission) }}</template></el-table-column>
                            </el-table>
                        </card>
                    </div>
                </div>
            </template>

            <p class="small text-muted" data-testid="aff-rep-footnote">{{ $t("marketing.affiliate.report.footnote") }}</p>
        </div>
    </div>
</template>

<script>
import { DatePicker, Select, Option, Table, TableColumn, Alert } from "element-ui";
import LineChart from "@/components/Charts/LineChart";
import { AffiliateService } from "@/resource";
import { isSchemaMissing } from "@/utils/marketing";
import { REPORT_CARDS, defaultRange, rangeOk, reportCardValue, reportChart, saveBlob } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";

// รายงาน Affiliate (Phase 3 spec 2026-10-13 §5.6).
export default {
    name: "MarketingAffiliateReport",
    components: {
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Alert.name]: Alert,
        LineChart
    },
    mixins: [affiliateMixin],
    data() {
        return {
            range: defaultRange(30),
            group: "day",
            exportReport: "series",
            loading: false,
            exporting: false,
            schemaMissing: false,
            rangeError: false,
            cards: null,
            topAffiliates: [],
            topTargets: [],
            cardKeys: REPORT_CARDS,
            chart: { labels: [], datasets: [] },
            chartKey: 0,
            chartOptions: { maintainAspectRatio: false, legend: { display: true, position: "bottom" } },
            loadedOnce: false
        };
    },
    created() {
        this.run();
    },
    activated() {
        if (this.loadedOnce) this.run();
    },
    methods: {
        cardValue(k) {
            return reportCardValue(k, this.cards);
        },
        params() {
            return { from: this.range[0], to: this.range[1] };
        },
        async run() {
            this.rangeError = !rangeOk(this.range, 366);
            if (this.rangeError) return;
            this.loading = true;
            try {
                const [sum, ta, tt] = await Promise.all([
                    AffiliateService.reportSummary({ ...this.params(), group: this.group }),
                    AffiliateService.reportTopAffiliates({ ...this.params(), limit: 20 }),
                    AffiliateService.reportTopTargets({ ...this.params(), limit: 20 })
                ]);
                const s = this.unwrap(sum) || {};
                this.cards = s.cards || {};
                this.chart = reportChart(s.series, {
                    clicks: this.$t("marketing.affiliate.report.col.clicks"),
                    orders: this.$t("marketing.affiliate.report.col.orders")
                });
                this.chartKey += 1;
                this.topAffiliates = this.unwrap(ta) || [];
                this.topTargets = this.unwrap(tt) || [];
                this.schemaMissing = false;
            } catch (err) {
                this.cards = null;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
                this.loadedOnce = true;
            }
        },
        async exportCsv() {
            if (!rangeOk(this.range, 366)) return;
            this.exporting = true;
            try {
                saveBlob(await AffiliateService.exportReportCsv({ ...this.params(), report: this.exportReport }), `affiliate-${this.exportReport}.csv`);
            } catch (err) {
                this.showError(err);
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}
</style>
